import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CustomInput from "../../Components/Generic/CustomInput";
import { useUserContext } from "../../context/useUserContext";
import { useNavigate, useOutletContext } from "react-router-dom";
import { showGenericError } from "../../helper/Notifications";
import { AnimatePresence } from "framer-motion";
import TwoFAModal from "./TwoFAModal";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import ChangePasswordCard from "./ChangePasswordCard";
import DeleteConfirmationChild from "../../Components/Generic/DeleteConfirmationWithChild";
import { REMOVE_ACCOUNT } from "../../constants/OptionsConstants";
import PageHeader from "../../Components/Layout/PageHeader";
import OutlineButton from "../../Components/Generic/OutlineButton";
import SecondaryButton from "../../Components/Generic/SecondaryButton";

const ProfilePage = () => {
  const { updateUser, user, removeAccount,  } = useUserContext();
  const [optRequired, setOtpRequired] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [infoChanges, setInfoChanges] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { setLoading } = useOutletContext();

  const navigate = useNavigate();

  const handleUpdateUser = () => {
    if (!name.trim()) {
      return;
    }
    const data = {};
    if (name != user.name) {
      data.name = name;
    }
    updateUser(
      data,
      (response) => {},
      (error) => {
        showGenericError();
      }
    );
  };

  const handleRemoveAccount = () => {
    setLoading(true);
    removeAccount(
      (response) => {
        setLoading(false);
        navigate("/login");
      },
      (error) => {
        setLoading(false);
        showGenericError();
      }
    );
  };

  const handle2FABtn = () => {
    setShow2FAModal(true);
  };

  const checkInfoChanges = () => {
    if (!user) {
      return;
    }
    if (name.trim() && name != user.name) {
      setInfoChanges(true);
    } else {
      setInfoChanges(false);
    }
  };

  const setInitialState = () => {
    setName(user.name);
    setEmail(user.email);
    setOtpRequired(user.otp_required_for_login);
  };

  useEffect(() => {
    setInitialState();
  }, [user]);

  useEffect(() => {
    checkInfoChanges();
  }, [name, email, user]);

  return (
    <Container>
      <AnimatePresence>
        {show2FAModal && <TwoFAModal setIsModalOpen={setShow2FAModal} />}
        {showChangePassword && (
          <ChangePasswordCard setIsModalOpen={setShowChangePassword} />
        )}
        {/* {showInfoModal &&
                    <RemoveAccountInfoModal
                        setIsModalOpen={setShowInfoModal}
                    />
                } */}
      </AnimatePresence>

      <PageContent>
        <PageHeader
          title="Profile"
          subtitle="Edit profile info, manage security, or delete your account."
        />
        <Content>
          {/* Account details */}
          <section className="flex flex-col gap-3">
            <h2 className="font-inter text-[20px] leading-[38px] font-semibold text-gray-900">
              Account details
            </h2>
            <div className="max-w-xl flex flex-col gap-4">
              <div className="flex flex-col gap-6">
                <CustomInput
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  label={"Name"}
                  placeholder={"Name"}
                  wrapperClassName="max-w-[400px]"
                />
              <CustomInput
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                label={"Email address"}
                placeholder={"Email"}
                type="email"
                wrapperClassName="max-w-[400px]"
                disabled
              />
              </div>
              {infoChanges && (
                <div>
                  <PrimaryButton text={"Save changes"} onClick={handleUpdateUser} />
                </div>
              )}
            </div>
          </section>

          {/* Security */}
          <section className="flex flex-col gap-3">
            <h2 className="font-inter text-[20px] leading-[38px] font-semibold text-gray-900">
              Security
            </h2>
            <div className="flex items-center gap-[10px]">
              <OutlineButton
                onClick={handle2FABtn}
                text={
                  user.otp_required_for_login
                    ? "Disable 2 Factor Authentication"
                    : "Enable 2 Factor Authentication"
                }
              />
              <OutlineButton
                onClick={() => setShowChangePassword(true)}
                text="Change Password"
              />
            </div>
          </section>

          {/* Danger zone */}
          <section className="flex flex-col gap-3">
            <h2 className="font-inter text-[20px] leading-[38px] font-semibold text-error-700">
              Danger Zone
            </h2>
            <p className="font-inter text-[14px] leading-5 text-gray-600 ">
              Once you delete your account, all your data and activity will be erased and you'll
              lose access to the platform forever. This action is irreversible.
            </p>
            <DeleteConfirmationChild
              open={deleteConfirmOpen}
              setOpen={setDeleteConfirmOpen}
              action={handleRemoveAccount}
              variant={REMOVE_ACCOUNT}
            >
              <SecondaryButton
                text="Delete Account"
                onClick={() => setDeleteConfirmOpen(true)}
              />
            </DeleteConfirmationChild>
          </section>
        </Content>
      </PageContent>
    </Container>
  );
};

export default ProfilePage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px; /* 24px from full PageHeader (title+subtitle) to content */
  overflow: hidden;
  padding: 32px 24px;
  position: relative;
  height: 100%;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
